import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import feedbackSurveyService from '@/services/feedbackSurveyService.ts';
import type {
  FeedbackSurveyAdminResults,
  FeedbackSurveyTextEntry,
  RatingStats,
} from '@/types/feedbackSurvey.ts';
import { useAlert } from '@/context/AlertContext.tsx';

// Pagina SuperUser: risultati anonimi del sondaggio di feedback MVP.
export default function SuperUserFeedbackPage() {
  const { t, i18n } = useTranslation();
  const { showAlert } = useAlert();
  const [data, setData] = useState<FeedbackSurveyAdminResults | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    feedbackSurveyService
      .getAdminResults()
      .then(setData)
      .catch(() => showAlert(t('feedbackSurvey.admin.loadError'), 'error'))
      .finally(() => setLoading(false));
  }, [showAlert, t]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) return null;

  const ratingBlocks: { key: string; stats: RatingStats }[] = [
    { key: 'listening', stats: data.listeningRating },
    { key: 'easeOfUse', stats: data.easeOfUseRating },
    { key: 'wouldPay', stats: data.wouldPayRating },
    { key: 'employerProvided', stats: data.employerProvidedRating },
    { key: 'recommend', stats: data.recommendRating },
  ];

  const dateFmt = new Intl.DateTimeFormat(i18n.resolvedLanguage ?? 'es', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return (
    <div className="mx-auto max-w-4xl animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-heading text-foreground">{t('feedbackSurvey.admin.title')}</h1>
        <p className="mt-2 text-muted-foreground">
          {t('feedbackSurvey.admin.subtitle')} · {t('feedbackSurvey.admin.totalResponses', { count: data.totalResponses })}
        </p>
      </div>

      {data.totalResponses === 0 ? (
        <p className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          {t('feedbackSurvey.admin.empty')}
        </p>
      ) : (
        <>
          <section className="mb-10 grid gap-4 sm:grid-cols-2">
            {ratingBlocks.map(({ key, stats }) => (
              <RatingCard
                key={key}
                title={t(`feedbackSurvey.q.${key}`)}
                stats={stats}
              />
            ))}
          </section>

          <section>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t('feedbackSurvey.admin.textResponsesTitle')}
            </h2>
            {data.textResponses.length === 0 ? (
              <p className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
                {t('feedbackSurvey.admin.noText')}
              </p>
            ) : (
              <div className="space-y-4">
                {data.textResponses.map((entry, idx) => (
                  <TextResponseCard
                    key={idx}
                    entry={entry}
                    dateLabel={dateFmt.format(new Date(entry.createdAt))}
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function RatingCard({ title, stats }: { title: string; stats: RatingStats }) {
  const max = Math.max(1, ...stats.distribution);
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-end justify-between">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="text-2xl font-heading text-primary">
          {stats.average.toFixed(1)}
          <span className="text-sm text-muted-foreground">/10</span>
        </p>
      </div>
      <div className="mt-3 flex items-end gap-1" aria-hidden>
        {stats.distribution.map((count, i) => (
          <div key={i} className="flex flex-1 flex-col items-center gap-1">
            <div
              className="w-full rounded-sm bg-primary/70"
              style={{ height: `${4 + (count / max) * 40}px` }}
              title={`${i + 1}: ${count}`}
            />
            <span className="text-[10px] text-muted-foreground">{i + 1}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TextResponseCard({
  entry,
  dateLabel,
}: {
  entry: FeedbackSurveyTextEntry;
  dateLabel: string;
}) {
  const { t } = useTranslation();
  const fields: { key: string; value?: string | null }[] = [
    { key: 'missingExpected', value: entry.missingExpected },
    { key: 'likedMost', value: entry.likedMost },
    { key: 'likedLeast', value: entry.likedLeast },
    { key: 'difficulties', value: entry.difficulties },
    { key: 'wouldPayReason', value: entry.wouldPayReason },
    { key: 'other', value: entry.otherComments },
  ].filter((f) => f.value && f.value.trim().length > 0);

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
        <span className="rounded-full bg-muted px-2 py-0.5 font-medium">
          {t(`feedbackSurvey.source.${entry.source.toLowerCase()}`, entry.source)}
        </span>
        <span>{dateLabel}</span>
      </div>
      <dl className="space-y-2">
        {fields.map((f) => (
          <div key={f.key}>
            <dt className="text-xs font-semibold text-muted-foreground">
              {t(`feedbackSurvey.q.${f.key}`)}
            </dt>
            <dd className="text-sm text-foreground whitespace-pre-wrap">{f.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
